import { collection, onSnapshot, orderBy, query } from 'firebase/firestore';
import { db } from '@/firebaseInit';
import { coll_admins, type AdminUserType } from '@/types/types_admin';
import { coll_invites, type InviteType } from '@/types/types_invite';
import { coll_partnerUsers, type PartnerUserType } from '@/types/types_partnerUser';

// Roster-wide reads for the admin accounts page: every account, every
// invitation, every admin, across all partners.
//
// The per-partner equivalents live in invitesRepo.ts. Kept apart on purpose —
// these three queries are readable only by an Eighty Days admin (see
// `firestore.rules`), where the partner-scoped ones are also served to partners
// themselves.
//
// Read-only, like invitesRepo: `partnerUsers` and `invites` are written
// exclusively by Cloud Functions, and `admins` by nobody but the console.

const adminsCollection = collection(db, coll_admins);
const invitesCollection = collection(db, coll_invites);
const partnerUsersCollection = collection(db, coll_partnerUsers);

/**
 * Subscribes to every partner account. Deliberately unordered: sorting by
 * partner needs the partner names, which the page has and Firestore does not.
 */
export function subscribeAllPartnerUsers(
  onChange: (users: PartnerUserType[]) => void,
  onError?: (error: Error) => void,
): () => void {
  return onSnapshot(
    partnerUsersCollection,
    (snap) => onChange(snap.docs.map((d) => ({ ...d.data(), id: d.id }) as PartnerUserType)),
    onError,
  );
}

/**
 * Subscribes to every invitation, newest first. A single-field order needs no
 * composite index, unlike the partner-scoped query in invitesRepo.
 */
export function subscribeAllInvites(
  onChange: (invites: InviteType[]) => void,
  onError?: (error: Error) => void,
): () => void {
  return onSnapshot(
    query(invitesCollection, orderBy('createdAt', 'desc')),
    (snap) => onChange(snap.docs.map((d) => ({ ...d.data(), id: d.id }) as InviteType)),
    onError,
  );
}

/** Subscribes to the Eighty Days admin roster. */
export function subscribeAdmins(
  onChange: (admins: AdminUserType[]) => void,
  onError?: (error: Error) => void,
): () => void {
  return onSnapshot(
    adminsCollection,
    (snap) => onChange(snap.docs.map((d) => ({ ...d.data(), id: d.id }) as AdminUserType)),
    onError,
  );
}
